import type { QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore'
import {
  createActivityExplore,
  deleteActivityExplore,
  fetchActivitiesByIds,
  fetchActivitiesPaginated,
  fetchActivity,
  fetchLatestActivities,
  fetchNewsCategories,
  fetchRecommendedByNewsCategory,
  observeActivityExplore,
  searchPdNews,
  type NewsFetchPolicy,
  type PaginatedResult,
  updateActivityExplore,
} from '@core/services/news/news-service'
import { ImageService } from '@core/services/image/image-service'
import {
  getAuthenticatedUidOrThrow,
  loadCurrentUserProfile,
} from '@core/services/user/user-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type {
  ActivityExplore,
  CreateActivityExploreInput,
  UpdateActivityExplorePatch,
} from '@domain/models/activity-explore'
import type { NewsCategory } from '@domain/models/news-category'

export type { PaginatedResult }
const imageService = ImageService.getInstance()

export class NewsFeature {
  public static async getLatestNewsActivities(
    count = 4,
    fetchPolicy: NewsFetchPolicy = 'default',
  ): Promise<ActivityExplore[]> {
    try {
      return await fetchLatestActivities(count, fetchPolicy)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch latest news activities.',
        underlyingException: error,
      })
    }
  }

  public static async getNewsActivity(
    activityId: string,
  ): Promise<ActivityExplore> {
    try {
      return await fetchActivity(activityId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch news activity.',
        underlyingException: error,
      })
    }
  }

  public static async getNewsActivitiesByIds(
    ids: string[],
  ): Promise<ActivityExplore[]> {
    try {
      return await fetchActivitiesByIds(ids)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch news activities by ids.',
        underlyingException: error,
      })
    }
  }

  public static async getNewsActivitiesPaginated(
    pageSize: number,
    lastDocument?: QueryDocumentSnapshot | null,
    newsCategoryId?: string | null,
    fetchPolicy: NewsFetchPolicy = 'default',
  ): Promise<PaginatedResult> {
    try {
      return await fetchActivitiesPaginated(
        pageSize,
        lastDocument,
        newsCategoryId,
        fetchPolicy,
      )
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch paginated news activities.',
        underlyingException: error,
      })
    }
  }

  public static async getNewsCategories(): Promise<NewsCategory[]> {
    try {
      return await fetchNewsCategories()
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to fetch news categories.',
        underlyingException: error,
      })
    }
  }

  public static async createNewsActivity(
    input: CreateActivityExploreInput,
  ): Promise<string> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      return await createActivityExplore(input, currentUser)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to create news activity.',
        underlyingException: error,
      })
    }
  }

  public static async updateNewsActivity(
    activityId: string,
    patch: UpdateActivityExplorePatch,
    existingActivity?: ActivityExplore,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await updateActivityExplore(activityId, patch, currentUser, existingActivity)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to update news activity "${activityId}".`,
        underlyingException: error,
      })
    }
  }

  public static async deleteNewsActivity(
    activityId: string,
    existingActivity?: ActivityExplore,
  ): Promise<void> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      const currentUser = await loadCurrentUserProfile(uid)
      await deleteActivityExplore(activityId, currentUser, existingActivity)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to delete news activity "${activityId}".`,
        underlyingException: error,
      })
    }
  }

  public static observeNewsActivity(
    activityId: string,
    callback: (activity: ActivityExplore | null) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    try {
      return observeActivityExplore(activityId, callback, onError)
    } catch (error) {
      onError?.(
        error instanceof CoreError
          ? error
          : new CoreError({
              cause: CoreErrorCause.Unknown,
              context: `Failed to observe news activity "${activityId}".`,
              underlyingException: error,
            }),
      )
      return () => {}
    }
  }

  public static async getRecommendedNewsByNewsCategory(
    newsCategoryId: string,
    count = 25,
  ): Promise<ActivityExplore[]> {
    try {
      return await fetchRecommendedByNewsCategory(newsCategoryId, count)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: `Failed to fetch recommended news for category "${newsCategoryId}".`,
        underlyingException: error,
      })
    }
  }

  public static async searchNews(query: string): Promise<string[]> {
    try {
      return await searchPdNews(query)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to search news.',
        underlyingException: error,
      })
    }
  }

  public static async uploadNewsImage(file: File): Promise<string> {
    try {
      const uid = getAuthenticatedUidOrThrow()
      return await imageService.uploadUserImage(uid, file)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.Unknown,
        context: 'Failed to upload news image.',
        underlyingException: error,
      })
    }
  }
}

export async function getLatestNewsActivities(
  count = 4,
  fetchPolicy: NewsFetchPolicy = 'default',
): Promise<ActivityExplore[]> {
  return NewsFeature.getLatestNewsActivities(count, fetchPolicy)
}

export async function getNewsActivity(
  activityId: string,
): Promise<ActivityExplore> {
  return NewsFeature.getNewsActivity(activityId)
}

export async function getNewsActivitiesByIds(
  ids: string[],
): Promise<ActivityExplore[]> {
  return NewsFeature.getNewsActivitiesByIds(ids)
}

export async function getNewsActivitiesPaginated(
  pageSize: number,
  lastDocument?: QueryDocumentSnapshot | null,
  newsCategoryId?: string | null,
  fetchPolicy: NewsFetchPolicy = 'default',
): Promise<PaginatedResult> {
  return NewsFeature.getNewsActivitiesPaginated(
    pageSize,
    lastDocument,
    newsCategoryId,
    fetchPolicy,
  )
}

export async function getNewsCategories(): Promise<NewsCategory[]> {
  return NewsFeature.getNewsCategories()
}

export async function createNewsActivity(
  input: CreateActivityExploreInput,
): Promise<string> {
  return NewsFeature.createNewsActivity(input)
}

export async function updateNewsActivity(
  activityId: string,
  patch: UpdateActivityExplorePatch,
  existingActivity?: ActivityExplore,
): Promise<void> {
  return NewsFeature.updateNewsActivity(activityId, patch, existingActivity)
}

export async function deleteNewsActivity(
  activityId: string,
  existingActivity?: ActivityExplore,
): Promise<void> {
  return NewsFeature.deleteNewsActivity(activityId, existingActivity)
}

export function observeNewsActivity(
  activityId: string,
  callback: (activity: ActivityExplore | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  return NewsFeature.observeNewsActivity(activityId, callback, onError)
}

export async function getRecommendedNewsByNewsCategory(
  newsCategoryId: string,
  count = 25,
): Promise<ActivityExplore[]> {
  return NewsFeature.getRecommendedNewsByNewsCategory(newsCategoryId, count)
}

export async function searchNews(query: string): Promise<string[]> {
  return NewsFeature.searchNews(query)
}

export async function uploadNewsImage(file: File): Promise<string> {
  return NewsFeature.uploadNewsImage(file)
}
